import {
  folderLabel,
  isBriefFolder,
  isIssueAttachmentsFolder,
} from "@/domains/documents/utils/briefFolder";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { TreeFolder } from "@/domains/documents/types";
import {
  FolderIcon,
  ChevronRight,
  ChevronDown,
  Check,
  ClipboardList,
  ListTodo,
  Loader2,
} from "lucide-react";

interface FolderPickerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectFolder: (folderId: string | null) => void;
  folders: TreeFolder[];
  title?: string;
  isLoading?: boolean;
  /** Names the top-level option; a view rooted at a folder passes that folder. */
  rootLabel?: string;
}

export function FolderPickerDialog({
  isOpen,
  onClose,
  onSelectFolder,
  folders,
  title = "Select Destination Folder",
  rootLabel = "Root Folder",
  isLoading = false,
}: FolderPickerDialogProps) {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  // `undefined` = nothing picked yet; `null` = explicit Root; `string` = folder id.
  // We need a third state because `null` previously meant *both* "user hasn't chosen"
  // and "user picked Root", which let an empty Select trigger a no-op move-to-root
  // and still fire a "moved successfully" toast (WHA-3261).
  const [selectedFolderId, setSelectedFolderId] = useState<string | null | undefined>(undefined);

  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const renderFolderTree = (folders: TreeFolder[], level = 0) => {
    return folders.map((folder) => (
      <div key={folder.id} style={{ marginLeft: `${level * 16}px` }}>
        <div
          className={`flex cursor-pointer items-center gap-2 rounded p-2 ${
            selectedFolderId === folder.id
              ? "bg-primary/10 text-primary ring-1 ring-primary/30"
              : "hover:bg-muted/50"
          }`}
          onClick={() => setSelectedFolderId(folder.id)}
        >
          {folder.children.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleFolder(folder.id);
              }}
              className="rounded p-1 hover:bg-muted"
            >
              {expandedFolders.has(folder.id) ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </button>
          )}
          {folder.children.length === 0 && <div className="w-5" />}

          {isBriefFolder(folder) ? (
            <ClipboardList className="h-4 w-4 text-primary" aria-hidden="true" />
          ) : isIssueAttachmentsFolder(folder) ? (
            <ListTodo className="h-4 w-4 text-primary" aria-hidden="true" />
          ) : (
            <FolderIcon className="h-4 w-4 text-primary" />
          )}
          <span className="text-sm">{folderLabel(folder)}</span>
          {selectedFolderId === folder.id && <Check className="ml-auto h-4 w-4 text-primary" />}
        </div>

        {expandedFolders.has(folder.id) && folder.children.length > 0 && (
          <div>{renderFolderTree(folder.children, level + 1)}</div>
        )}
      </div>
    ));
  };

  const handleSelect = () => {
    if (selectedFolderId === undefined) return;
    onSelectFolder(selectedFolderId);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="max-h-96 overflow-y-auto rounded-md border p-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading folders…</span>
            </div>
          ) : (
            <>
              {/* Root option */}
              <div
                className={`mb-2 flex cursor-pointer items-center gap-2 rounded p-2 ${
                  selectedFolderId === null
                    ? "bg-primary/10 text-primary ring-1 ring-primary/30"
                    : "hover:bg-muted/50"
                }`}
                onClick={() => setSelectedFolderId(null)}
              >
                <FolderIcon className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">{rootLabel}</span>
                {selectedFolderId === null && <Check className="ml-auto h-4 w-4 text-primary" />}
              </div>

              {folders.length > 0 ? (
                renderFolderTree(folders)
              ) : (
                <div className="py-4 text-center text-muted-foreground">No folders available</div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSelect} disabled={isLoading || selectedFolderId === undefined}>
            Select Folder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
